import styled, { CSSObject } from '@emotion/styled'
import { withClass } from '@news-mono/web-common'
import { Breakpoints } from '../../__styling/settings/breakpoints'
import { colors } from '../../__styling/settings/colors'
import { zIndex } from '../../__styling/settings/z-index'
import {
    breakpoint,
    breakpointMax,
    getSmallestBreakpointValue,
} from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { transition } from '../../__styling/style-mixins'
import { themedValue } from '../../__styling/themed-value'
import { IconComment } from '../../icons/IconComment/IconComment'
import { IconPremium } from '../../icons/IconPremium/IconPremium'
import { EditorialLabel } from '../../typography/EditorialLabel/EditorialLabel'
import {
    KickerBreakpoints,
    KickerPosition,
    KickerSizeOptions,
    KickerStyleGuide,
    KickerType,
    KickerVisibility,
} from './Kicker'

export interface StyledKickerProps {
    kickerVisibility?: KickerVisibility
    kickerPosition?: KickerPosition
    size?: KickerSizeOptions
    kickerType?: KickerType
    /** Only used on TW/PN in Olympics collection */
    kickerStyleGuide?: KickerStyleGuide
}

export type BreakpointKey = keyof Breakpoints
export const kickerBreakpoints: KickerBreakpoints[] = [
    'initial',
    'xs',
    'sm',
    'md',
]

export function getVisibilityStyles({
    kickerVisibility,
}: StyledKickerProps): CSSObject {
    if (kickerVisibility === 'hidden') {
        return { display: 'none' }
    }

    if (!kickerVisibility || kickerVisibility === 'visible') {
        return { display: 'inline-block' } // default
    }

    return (
        kickerVisibility &&
        Object.entries(kickerVisibility)
            .slice(1)
            .reduce<CSSObject>(
                (styles, [breakpointValue, visibilityForBreakpoint]) => {
                    styles[breakpoint(breakpointValue as BreakpointKey)] = {
                        display:
                            visibilityForBreakpoint === 'hidden'
                                ? 'none'
                                : 'inline-block',
                    }

                    return styles
                },
                {},
            )
    )
}

export const StyledKicker = styled('span')<StyledKickerProps>(
    getVisibilityStyles,
    // General styles for all mastheads
    ({ kickerVisibility, size, theme }) => ({
        borderRadius: theme.radius?.kicker,
        lineHeight: theme.radius ? (size === 'large' ? 1.9 : 1.7) : 1,
        textTransform: 'uppercase',
        marginBottom: theme.margins.xs,
        ...transition({ properties: ['background, color'] }),

        // Mobile styling
        [breakpointMax('xs')]: {
            display:
                getSmallestBreakpointValue(kickerVisibility) === 'hidden'
                    ? 'none'
                    : 'inline-block',
        },
    }),
    // The West styles
    ({ theme, kickerStyleGuide, kickerType, kickerVisibility }) =>
        theme.kind === 'thewest' && {
            marginBottom: theme.margins.xs,
            fontSize: calcRem(11),
            fontFamily: theme.fonts.sansSerif,
            fontWeight: 500,
            color: theme.colors.text.secondary,
            padding: kickerType === 'block' ? calcRem(5, 10) : undefined,
            backgroundColor:
                kickerType === 'block' ? colors.thewest.westblue : undefined,
            display: 'inline-flex',
            alignItems: 'center',
            gap: calcRem(4),
            lineHeight: calcRem(14), // To suit 14px icon height

            // Olympic styles for The West
            ...(kickerStyleGuide === 'olympics'
                ? {
                      flexDirection: 'row-reverse',
                      justifyContent: 'flex-end',
                      width: '100%',
                      color: colors.olympics.kicker.thewest,
                  }
                : {}),

            // Mobile styling
            [breakpointMax('xs')]: {
                display:
                    getSmallestBreakpointValue(kickerVisibility) === 'hidden'
                        ? 'none'
                        : 'inline-flex',
            },
        },
    // PerthNow specific styles
    ({ theme, kickerStyleGuide, kickerVisibility, size }) =>
        theme.kind === 'perthnow' && {
            borderRadius: theme.radius?.kicker,
            display:
                getSmallestBreakpointValue(kickerVisibility) === 'hidden'
                    ? 'none'
                    : 'inline-block',
            lineHeight: theme.radius ? (size === 'large' ? 1.9 : 1.7) : 1,
            marginBottom: theme.margins.xs,
            textTransform: 'none',
            fontSize: size === 'large' ? calcRem(16) : calcRem(13),
            fontFamily: theme.fonts.sansSerif,
            fontWeight: 600,
            color: theme.colors.text.tertiary,
            padding:
                size === 'large'
                    ? // Size = large
                      theme.radius
                        ? `0 ${calcRem(12)} 0 ${calcRem(12)}`
                        : calcRem(7)
                    : // Size = default
                    theme.radius
                    ? `0 ${calcRem(7)} 0 ${calcRem(7)}`
                    : calcRem(4),
            backgroundColor:
                theme.colors.actions.button.primary.background.default,

            // Olympic styles for PerthNow
            ...(kickerStyleGuide === 'olympics'
                ? {
                      display:
                          getSmallestBreakpointValue(kickerVisibility) ===
                          'hidden'
                              ? 'none'
                              : 'inline-flex',
                      position: 'relative',
                      height: 'fit-content',
                      backgroundColor: colors.olympics.link,
                      color: colors.olympics.kicker.perthnow,
                      fontSize: calcRem(13),
                      marginBottom: calcRem(4),
                      lineHeight: calcRem(16),
                      borderRadius: 0,
                      padding: calcRem(2, 4, 2, 4),
                  }
                : {}),
        },
    // 7NEWS specific styles
    ({ theme, kickerType }) =>
        theme.kind === 'sevennews' && {
            fontSize: calcRem(12),
            fontWeight: 500,
            color: themedValue(theme, {
                sevennews: (section) =>
                    kickerType === 'text'
                        ? section.cards.kicker.primaryColor
                        : colors.white,
                fallback: undefined,
            }),
            padding: kickerType === 'block' ? calcRem(4, 9, 3) : undefined,
            backgroundColor: themedValue(theme, {
                sevennews:
                    kickerType === 'block'
                        ? (section) => section.cards.kicker.primaryColor
                        : undefined,
                fallback: undefined,
            }),
            zIndex: zIndex.sevennews.cardKicker,
            fontFamily: theme.fonts.sansSerif,
        },
    // General styles for all mastheads for each kickerPosition
    ({ kickerPosition }) => {
        switch (kickerPosition) {
            case 'topleft':
                return {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }
            case 'bottomleft':
                return {
                    position: 'absolute',
                    marginTop: calcRem(-34),
                }
            case 'bottomleftinset-hero': {
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: 0,
                }
            }
            case 'bottomleftinset-herovideo': {
                return {
                    position: 'absolute',
                    left: calcRem(16),
                    bottom: calcRem(16),
                    marginBottom: 0,
                }
            }
            case 'mobilebottomleft-insetdesktop':
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: 0,

                    [breakpoint('sm')]: {
                        left: calcRem(16),
                    },
                }
            case 'bottomleftinset':
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: calcRem(16),
                }

            case 'topleftinset':
                return {
                    position: 'absolute',
                    top: calcRem(16),
                    left: calcRem(16),
                }
            default:
                return {}
        }
    },
)

export const StyledKickerText = styled('span')<{
    kickerStyleGuide?: KickerStyleGuide
}>(({ kickerStyleGuide, theme }) => [
    {
        display: kickerStyleGuide === 'olympics' ? 'flex' : undefined,
        height: kickerStyleGuide === 'olympics' ? 'fit-content' : undefined,
    },
    // Styles for The West
    theme.kind === 'thewest' && {
        alignSelf: 'center',
        alignItems: 'center',
        display: kickerStyleGuide === 'olympics' ? 'flex' : 'inline-flex',
        gap: calcRem(8),
    },
])

export interface StyledPremiumIconProps {
    width: number
    height: number
    fill?: string
}

export const StyledPremiumIcon = styled(IconPremium)<StyledPremiumIconProps>(
    ({ width, height, fill, theme }) => ({
        width,
        height,
        verticalAlign: 'text-bottom',
        backgroundColor: theme.colors.icons?.background || colors.white,
        borderRadius: 10,
    }),
)

export const StyledCommentsIconClassName = 'StyledCommentsIcon'

export const StyledCommentsIcon = withClass(StyledCommentsIconClassName)(
    styled(IconComment)(({ theme }) => ({
        width: 14,
        height: 14,
        strokeWidth: 0,
    })),
)

/**
 * This editorial label only appears on The West next to kickers
 */
export const StyledEditorialLabel = styled(EditorialLabel)({
    display: 'inline-block',
    paddingRight: calcRem(8),

    '&::after': {
        content: `''`,
        display: 'inline-block',
        width: 1,
        height: calcRem(13),
        transform: 'translate(4px, 2px)',
        background: colors.thewest.greyElephant,
    },
})
